(() => {
  const moneyFormatNode = (root) => {
    const node = root.querySelector('[data-balao-money-format]');
    if (!node) return '{{amount}}';
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return '{{amount}}';
    }
  };

  const formatMoney = (cents, format) => {
    if (typeof cents !== 'number') return '';
    const value = (cents / 100).toFixed(2).replace('.', ',');
    const withThousands = value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return String(format || 'R$ {{amount}}')
      .replace('{{amount_with_comma_separator}}', withThousands)
      .replace('{{amount_no_decimals}}', String(Math.round(cents / 100)))
      .replace('{{amount}}', withThousands);
  };

  class BalaoProductPage {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-balao-product-form]');
      this.variantInput = root.querySelector('[data-balao-variant-id]');
      this.priceCurrent = root.querySelector('[data-balao-price-current]');
      this.priceCompare = root.querySelector('[data-balao-price-compare]');
      this.stock = root.querySelector('[data-balao-stock]');
      this.addBtn = root.querySelector('[data-balao-add]');
      this.addLabel = root.querySelector('[data-balao-add-label]');
      this.sticky = root.querySelector('[data-balao-sticky-cta]');
      this.stickyPrice = root.querySelector('[data-balao-sticky-price]');
      this.stickyAdd = root.querySelector('[data-balao-sticky-add]');
      this.qtyInput = root.querySelector('[data-balao-qty-input]');
      this.thumbs = [...root.querySelectorAll('[data-balao-thumb]')];
      this.slides = [...root.querySelectorAll('[data-balao-slide]')];
      this.carouselRoot = root.querySelector('[data-balao-gallery] [data-balao-carousel]');
      this.moneyFormat = moneyFormatNode(root);
      this.variants = this.#parseVariants();
      this.optionInputs = [...root.querySelectorAll('[data-balao-option]')];

      if (!this.form || !this.variantInput) return;

      this.#bindOptions();
      this.#bindQty();
      this.#bindThumbs();
      this.#bindSticky();
      this.#bindAddToCart();
      this.#syncFromSelection(false);
    }

    #parseVariants() {
      const node = this.root.querySelector('[data-balao-variants-json]');
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return [];
      }
    }

    #selectedOptions() {
      const selected = {};
      this.optionInputs.forEach((input) => {
        if (input.checked) {
          selected[input.dataset.optionPosition] = input.value;
        }
      });
      return selected;
    }

    #findVariant() {
      const selected = this.#selectedOptions();
      return this.variants.find((variant) => {
        const o1 = selected['1'] == null || variant.option1 === selected['1'];
        const o2 = selected['2'] == null || variant.option2 === selected['2'];
        const o3 = selected['3'] == null || variant.option3 === selected['3'];
        return o1 && o2 && o3;
      });
    }

    #bindOptions() {
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const label = input.closest('label');
          const fieldset = input.closest('fieldset');
          if (fieldset) {
            fieldset.querySelectorAll('label').forEach((item) => item.classList.remove('is-selected'));
            label?.classList.add('is-selected');
            const valueNode = fieldset.querySelector('[data-balao-option-value]');
            if (valueNode) valueNode.textContent = input.value;
          }
          this.#syncFromSelection(true);
        });
      });
    }

    #bindQty() {
      const minus = this.root.querySelector('[data-balao-qty-minus]');
      const plus = this.root.querySelector('[data-balao-qty-plus]');
      minus?.addEventListener('click', () => {
        const next = Math.max(1, Number(this.qtyInput.value || 1) - 1);
        this.qtyInput.value = String(next);
      });
      plus?.addEventListener('click', () => {
        const next = Math.max(1, Number(this.qtyInput.value || 1) + 1);
        this.qtyInput.value = String(next);
      });
    }

    #bindThumbs() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = Number(thumb.dataset.index || 0);
          this.#goToMedia(thumb.dataset.mediaId, index);
        });
      });
    }

    #goToMedia(mediaId, index) {
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaId === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.slides.forEach((slide) => {
        slide.classList.toggle('is-active', slide.dataset.mediaId === String(mediaId));
      });

      if (this.carouselRoot && typeof index === 'number') {
        const instance = window.BalaoCarouselInstances?.get(this.carouselRoot);
        if (instance?.goTo) {
          instance.goTo(index);
        }
      }
    }

    #bindSticky() {
      if (!this.sticky || !this.addBtn) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          const show = !entry.isIntersecting && window.innerWidth < 900;
          this.sticky.hidden = !show;
        },
        { threshold: 0, rootMargin: '0px 0px -20% 0px' }
      );
      observer.observe(this.addBtn);

      this.stickyAdd?.addEventListener('click', () => {
        if (this.addBtn.disabled) return;
        this.form?.requestSubmit?.() || this.addBtn.click();
      });
    }

    #bindAddToCart() {
      this.form?.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (this.addBtn?.disabled) return;

        const formData = new FormData(this.form);
        this.addBtn.classList.add('is-loading');
        this.addBtn.disabled = true;

        try {
          const response = await fetch(window.Shopify?.routes?.root
            ? `${window.Shopify.routes.root}cart/add.js`
            : '/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });

          if (!response.ok) throw new Error('add_failed');

          this.addLabel.textContent = 'Adicionado!';
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          document.dispatchEvent(new CustomEvent('balao:cart:added'));
          window.setTimeout(() => {
            if (this.addLabel) this.addLabel.textContent = 'Adicionar ao carrinho';
            this.addBtn.disabled = false;
            this.addBtn.classList.remove('is-loading');
          }, 1400);
        } catch (error) {
          this.addLabel.textContent = 'Tentar novamente';
          this.addBtn.disabled = false;
          this.addBtn.classList.remove('is-loading');
        }
      });
    }

    #syncFromSelection(updateUrl) {
      const variant = this.#findVariant();
      if (!variant) {
        this.#setUnavailable();
        return;
      }

      this.variantInput.value = String(variant.id);
      this.#updatePrice(variant);
      this.#updateStock(variant);
      this.#updateButtons(variant);
      if (variant.featured_media_id) {
        const thumb = this.thumbs.find((item) => item.dataset.mediaId === String(variant.featured_media_id));
        const index = thumb ? Number(thumb.dataset.index || 0) : 0;
        this.#goToMedia(variant.featured_media_id, index);
      }

      if (updateUrl && variant.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      }
    }

    #updatePrice(variant) {
      if (this.priceCurrent) {
        this.priceCurrent.textContent = formatMoney(variant.price, this.moneyFormat);
      }
      if (this.stickyPrice) {
        this.stickyPrice.textContent = formatMoney(variant.price, this.moneyFormat);
      }
      if (this.priceCompare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          this.priceCompare.hidden = false;
          this.priceCompare.textContent = formatMoney(variant.compare_at_price, this.moneyFormat);
        } else {
          this.priceCompare.hidden = true;
          this.priceCompare.textContent = '';
        }
      }
    }

    #updateStock(variant) {
      if (!this.stock) return;
      let html = '<span class="balao-badge balao-badge--sold">Esgotado</span>';
      if (variant.available) {
        const low =
          variant.inventory_management &&
          variant.inventory_quantity > 0 &&
          variant.inventory_quantity <= 5;
        html = low
          ? '<span class="balao-badge balao-badge--warn">Últimas unidades</span>'
          : '<span class="balao-badge balao-badge--ok">Em estoque</span>';
      }
      this.stock.innerHTML = html;
    }

    #updateButtons(variant) {
      const enabled = Boolean(variant.available);
      if (this.addBtn) {
        this.addBtn.disabled = !enabled;
        if (this.addLabel) {
          this.addLabel.textContent = enabled ? 'Adicionar ao carrinho' : 'Esgotado';
        }
      }
      if (this.stickyAdd) this.stickyAdd.disabled = !enabled;
    }

    #setUnavailable() {
      if (this.addBtn) {
        this.addBtn.disabled = true;
        if (this.addLabel) this.addLabel.textContent = 'Indisponível';
      }
      if (this.stickyAdd) this.stickyAdd.disabled = true;
      if (this.stock) {
        this.stock.innerHTML = '<span class="balao-badge balao-badge--sold">Indisponível</span>';
      }
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-balao-product-page]').forEach((root) => {
      if (root.dataset.balaoProductReady) return;
      root.dataset.balaoProductReady = 'true';
      new BalaoProductPage(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
